import {
  Component,
  type ErrorInfo,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  hardReloadApp,
  isStaleChunkError,
  handleStaleChunk,
} from "../utils/staleChunkRecovery";

/* ==========================================================================
   CRASH LINE TYPE
   ========================================================================== */

interface CrashLine {
  text: string;
  delay: number;
  cls?: string;
}

/* ==========================================================================
   DIAGNOSTIC HELPERS — mix real error data with fake kernel‑panic fluff
   ========================================================================== */

function hex(n: number): string {
  return "0x" + n.toString(16).toUpperCase().padStart(8, "0");
}

function fakeAddr(): string {
  return hex(Math.floor(Math.random() * 0xffffffff));
}

function buildCrashLines(error: Error, componentStack?: string): CrashLine[] {
  const lines: CrashLine[] = [];
  const push = (text: string, delay = 60, cls?: string) =>
    lines.push({ text, delay, cls });

  // Header
  push("", 200);
  push("*** SYSTEM HALT ***", 0, "crash-header");
  push("", 150);
  push(
    "KERNEL PANIC — fatal exception in user‑space process",
    200,
    "crash-highlight"
  );
  push(
    "A problem has been detected and SystemOS has been shut down to prevent",
    80
  );
  push("damage to your desktop environment.", 80);
  push("", 150);

  // Fake register dump
  push("Processor state:", 80);
  push(`  EIP: ${fakeAddr()}   ESP: ${fakeAddr()}   EBP: ${fakeAddr()}`, 60);
  push(`  EAX: ${fakeAddr()}   EBX: ${fakeAddr()}   ECX: 0x00000000`, 60);
  push(`  EFLAGS: 0xDEADBEEF   CR3: ${fakeAddr()}`, 60);
  push("", 100);

  // Real error name + message
  const errName = error.name || "Error";
  const errMsg = error.message || "Unknown error";
  push(`FAULT: ${errName}`, 100, "crash-highlight");
  push(`  ${errMsg.slice(0, 160)}`, 80);
  if (errMsg.length > 160) {
    push(`  ${errMsg.slice(160, 320)}`, 60);
  }
  push("", 100);

  // Real stack trace (sanitized)
  if (error.stack) {
    push("Stack trace:", 80);
    const stackLines = error.stack
      .split("\n")
      .filter((l) => l.trim().startsWith("at ") || l.trim().startsWith("@"))
      .slice(0, 8);
    for (const line of stackLines) {
      push(`  ${line.trim()}`, 50);
    }
    if (error.stack.split("\n").length > 10) {
      push("  ... (truncated)", 30);
    }
  }
  push("", 100);

  // Component stack (React-specific, very helpful)
  if (componentStack) {
    push("Component tree:", 80);
    const compLines = componentStack
      .split("\n")
      .filter((l) => l.trim())
      .slice(0, 6);
    for (const line of compLines) {
      push(`  ${line.trim()}`, 40);
    }
    push("", 100);
  }

  // Fake memory scan
  push("Scanning memory regions ...", 300);
  push(
    `  Heap:  ${Math.floor(Math.random() * 64 + 16)}MB used / 128MB allocated`,
    80
  );
  push(`  Stack: ${Math.floor(Math.random() * 900 + 100)} frames deep`, 80);
  push(`  DOM:   ${document.querySelectorAll("*").length} nodes allocated`, 80);
  push("", 100);

  // Fake module list
  push("Loaded modules:", 80);
  push("  react@18.x ........................ OK", 40);
  push("  react-dom@18.x .................... OK", 40);
  push("  vite@7.x .......................... OK", 40);
  push(
    `  window-manager .................... FAULT at ${fakeAddr()}`,
    40,
    "crash-highlight"
  );
  push("", 100);

  // Fake diagnostic collection
  push("Collecting diagnostic data:", 100);
  push("  Core dump ......................... saved to /var/crash/core.0", 200);
  push("  Event log ......................... 0 warnings, 1 critical", 150);
  push("  Render tree snapshot .............. captured", 150);
  push("", 100);

  // Error code
  const errorCode = `SOD-${(
    (errName.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 9000) +
    1000
  ).toString()}`;
  push(`Stop code: ${errorCode}`, 80, "crash-highlight");
  push("", 200);

  return lines;
}

/* ==========================================================================
   CRASH SCREEN (function component — rendered by ErrorBoundary)
   ========================================================================== */

function CrashScreen({
  error,
  componentStack,
  onReboot,
}: {
  error: Error;
  componentStack?: string;
  onReboot: () => void;
}) {
  const [visibleLines, setVisibleLines] = useState(0);
  const [phase, setPhase] = useState<"panic" | "reboot" | "booting">("panic");
  const [countdown, setCountdown] = useState(5);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const mountedRef = useRef(true);
  const linesRef = useRef<CrashLine[]>(buildCrashLines(error, componentStack));

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      timersRef.current.forEach(clearTimeout);
    };
  }, []);

  // Phase 1: type out panic lines
  useEffect(() => {
    if (phase !== "panic") return;
    const allLines = linesRef.current;

    let elapsed = 300;
    allLines.forEach((line, i) => {
      elapsed += line.delay;
      const t = setTimeout(() => {
        if (mountedRef.current) setVisibleLines(i + 1);
      }, elapsed);
      timersRef.current.push(t);
    });

    elapsed += 600;
    const t = setTimeout(() => {
      if (mountedRef.current) setPhase("reboot");
    }, elapsed);
    timersRef.current.push(t);
  }, [phase]);

  // Phase 2: countdown
  useEffect(() => {
    if (phase !== "reboot") return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setPhase("booting");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase]);

  // Phase 3: trigger reboot
  useEffect(() => {
    if (phase !== "booting") return;
    const t = setTimeout(onReboot, 600);
    timersRef.current.push(t);
  }, [phase, onReboot]);

  // Allow click/key to skip countdown
  useEffect(() => {
    if (phase !== "reboot") return;
    const skip = () => setPhase("booting");
    window.addEventListener("click", skip);
    window.addEventListener("keydown", skip);
    return () => {
      window.removeEventListener("click", skip);
      window.removeEventListener("keydown", skip);
    };
  }, [phase]);

  const allLines = linesRef.current;

  return (
    <div
      className="crash-screen"
      style={{
        opacity: phase === "booting" ? 0 : 1,
        transition: "opacity 0.5s ease-in-out",
      }}
    >
      <div className="crash-content">
        {/* Scanline overlay for CRT feel */}
        <div className="crash-scanlines" />

        {allLines.slice(0, visibleLines).map((line, i) => (
          <div key={i} className={`crash-line ${line.cls ?? ""}`}>
            {line.text || "\u00A0"}
          </div>
        ))}

        {phase === "reboot" && (
          <div className="crash-reboot">
            <span className="crash-highlight">
              System will restart in {countdown}s...
            </span>
            <span className="crash-hint">Press any key to restart now</span>
          </div>
        )}

        {phase === "booting" && (
          <div className="crash-line crash-highlight">Restarting...</div>
        )}

        {phase === "panic" && <span className="crash-cursor">_</span>}
      </div>
    </div>
  );
}

/* ==========================================================================
   RECOVERY SCREEN — shown when the app is stuck in a crash loop
   ========================================================================== */

function RecoveryScreen({
  onClearAndReload,
}: {
  onClearAndReload: () => void;
}) {
  const handleClear = useCallback(() => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      // Best effort
    }
    onClearAndReload();
  }, [onClearAndReload]);

  const handleRestart = useCallback(async () => {
    await hardReloadApp();
  }, []);

  return (
    <div
      className="crash-screen"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        className="crash-content"
        style={{ maxWidth: 520, textAlign: "center" }}
      >
        <div className="crash-line crash-header">*** SYSTEM RECOVERY ***</div>
        <div className="crash-line" style={{ marginTop: 16 }}>
          &nbsp;
        </div>
        <div className="crash-line crash-highlight">
          Multiple consecutive crashes detected — automatic restart disabled.
        </div>
        <div className="crash-line" style={{ marginTop: 8 }}>
          This usually means corrupted local data is causing a persistent error.
        </div>
        <div className="crash-line">
          You can restart, or clear all local data and start fresh.
        </div>
        <div className="crash-line" style={{ marginTop: 24 }}>
          &nbsp;
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button
            onClick={handleRestart}
            style={{
              padding: "8px 20px",
              fontSize: 14,
              fontFamily: "monospace",
              background: "transparent",
              color: "#4ade80",
              border: "1px solid #4ade80",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            Restart
          </button>
          <button
            onClick={handleClear}
            style={{
              padding: "8px 20px",
              fontSize: 14,
              fontFamily: "monospace",
              background: "#dc2626",
              color: "#fff",
              border: "1px solid #dc2626",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            Clear Data &amp; Reload
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   ERROR BOUNDARY (class component — React requirement)
   ========================================================================== */

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | undefined;
  crashCount: number;
}

/** After this many consecutive crashes, stop auto-rebooting */
const MAX_AUTO_REBOOTS = 3;

/** If children render successfully for this long, reset the crash counter */
const STABILITY_MS = 5_000;

export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    componentStack: undefined,
    crashCount: 0,
  };

  private stabilityTimer: ReturnType<typeof setTimeout> | null = null;

  static getDerivedStateFromError(
    error: Error
  ): Pick<State, "hasError" | "error"> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // If this looks like a stale deployment error, skip the crash screen
    // entirely and reload to pick up fresh assets. The user should never
    // see the panic screen for something we can silently recover from.
    if (isStaleChunkError(error)) {
      if (handleStaleChunk()) return; // reload in progress — bail out
    }

    // Clear any pending stability timer — we just crashed again
    if (this.stabilityTimer) {
      clearTimeout(this.stabilityTimer);
      this.stabilityTimer = null;
    }

    this.setState((prev) => ({
      componentStack: info.componentStack ?? undefined,
      crashCount: prev.crashCount + 1,
    }));
    console.error("[SystemOS Crash]", error, info.componentStack);
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    // When we transition FROM error TO children rendering, start a stability timer.
    // If children stay alive for STABILITY_MS, reset crashCount to 0.
    if (prevState.hasError && !this.state.hasError) {
      this.stabilityTimer = setTimeout(() => {
        this.stabilityTimer = null;
        this.setState({ crashCount: 0 });
      }, STABILITY_MS);
    }
  }

  componentWillUnmount() {
    if (this.stabilityTimer) {
      clearTimeout(this.stabilityTimer);
    }
  }

  handleReboot = async () => {
    await hardReloadApp();
  };

  handleClearAndReload = async () => {
    await hardReloadApp();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // If we've crashed too many times in a row, show recovery screen
      // instead of the auto-rebooting panic screen
      if (this.state.crashCount >= MAX_AUTO_REBOOTS) {
        return <RecoveryScreen onClearAndReload={this.handleClearAndReload} />;
      }

      return (
        <CrashScreen
          error={this.state.error}
          componentStack={this.state.componentStack}
          onReboot={this.handleReboot}
        />
      );
    }
    return this.props.children;
  }
}
